import { ArrowRight, MessageCircle, Shield, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";

export const HeroSection = ({ onGetQuote }) => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1752119491943-69bbabe34436')",
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Text Content */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6 opacity-0 animate-fade-in-up">
              <Shield className="h-4 w-4" />
              Trusted by Hundreds of South Carolina Families
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-foreground leading-tight mb-6 opacity-0 animate-fade-in-up animation-delay-100">
              Insurance That Feels Like{" "}
              <span className="text-primary">Family</span>
            </h1>

            <p className="text-base md:text-lg text-muted-foreground mb-8 max-w-xl mx-auto lg:mx-0 opacity-0 animate-fade-in-up animation-delay-200">
              Protecting South Carolina families for over 10 years. Get personalized
              coverage for your home, auto, and life with a team that truly cares.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start opacity-0 animate-fade-in-up animation-delay-300">
              <Button
                data-testid="hero-get-quote-button"
                onClick={onGetQuote}
                size="lg"
                className="rounded-full bg-accent hover:bg-accent/90 text-white px-8 py-6 text-base hover:scale-105 transition-transform duration-200"
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Get a Free Quote
              </Button>
              <Button
                data-testid="hero-learn-more-button"
                variant="outline"
                size="lg"
                onClick={() => {
                  document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="rounded-full border-primary text-primary hover:bg-primary hover:text-white px-8 py-6 text-base"
              >
                Learn More
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="flex items-center justify-center lg:justify-start gap-8 mt-10 pt-10 border-t border-border/50 opacity-0 animate-fade-in-up animation-delay-400">
              <div className="text-center">
                <div className="font-heading text-2xl font-bold text-primary">10+</div>
                <div className="text-xs text-muted-foreground">Years Serving SC</div>
              </div>
              <div className="h-8 w-px bg-border" />
              <div className="text-center">
                <div className="font-heading text-2xl font-bold text-primary">24/7</div>
                <div className="text-xs text-muted-foreground">Claims Support</div>
              </div>
            </div>
          </div>

          {/* Right - Chat Starter Card */}
          <div className="hidden lg:block">
            <div
              data-testid="hero-chat-starter"
              className="bg-white rounded-3xl shadow-xl p-8 border border-border max-w-md mx-auto opacity-0 animate-fade-in-up animation-delay-200"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="h-14 w-14 rounded-full bg-primary flex items-center justify-center">
                  <MessageCircle className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-foreground">
                    Get Your Quote
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Chat with our friendly assistant
                  </p>
                </div>
              </div>

              <p className="text-foreground mb-6">
                Hi! 👋 I'm here to help you find the perfect coverage. What would you
                like to protect today?
              </p>

              <div className="space-y-3">
                <button
                  data-testid="hero-quick-home"
                  onClick={onGetQuote}
                  className="w-full flex items-center gap-3 p-4 rounded-xl bg-secondary hover:bg-secondary/80 transition-colors text-left group"
                >
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <span className="text-lg">🏠</span>
                  </div>
                  <div>
                    <div className="font-medium text-foreground">Home Insurance</div>
                    <div className="text-sm text-muted-foreground">
                      Protect your home & belongings
                    </div>
                  </div>
                </button>

                <button
                  data-testid="hero-quick-auto"
                  onClick={onGetQuote}
                  className="w-full flex items-center gap-3 p-4 rounded-xl bg-secondary hover:bg-secondary/80 transition-colors text-left group"
                >
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <span className="text-lg">🚗</span>
                  </div>
                  <div>
                    <div className="font-medium text-foreground">Auto Insurance</div>
                    <div className="text-sm text-muted-foreground">
                      Coverage for your vehicles
                    </div>
                  </div>
                </button>

                <button
                  data-testid="hero-quick-life"
                  onClick={onGetQuote}
                  className="w-full flex items-center gap-3 p-4 rounded-xl bg-secondary hover:bg-secondary/80 transition-colors text-left group"
                >
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Heart className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-medium text-foreground">Life Insurance</div>
                    <div className="text-sm text-muted-foreground">
                      Secure your family's future
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
